import { clearAllBodyScrollLocks } from "body-scroll-lock";
import classNames from "classnames";
import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import iconXClose from "../../../../img/icons/x-close.svg";
import { ConfiguratorTypes } from "../../../constants";
import { IAddon } from "../../../models/catalogue";
import { IProductID } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { defaults } from "../defaults";
import {
    hideUpsellInfoModal,
    setSelectedUpgrade,
    updateSelectedAddons,
} from "../reducers";
import {
    baseVariantSelector,
    getUpsellInfoModal,
    rootProductSelector,
    upgradeBundlesSelector,
} from "../selectors";
import { UpsellInfoModalContent } from "./ConfiguratorUpsellInfoModalContent";

import styles from "./ConfiguratorUpsellInfoModal.module.scss";

const onClosePanel = () => {
    const document = getDocument();
    if (document) {
        document.body.classList.remove("configurator-panel-open");
    }
};

const onOpenPanel = () => {
    const document = getDocument();
    if (document) {
        document.body.classList.add("configurator-panel-open");
    }
};

export const UpsellInfoModal = (props: {
    configuratorType: ConfiguratorTypes;
    selectedAddons: readonly IAddon[];
    selectedUpgrade: IProductID | null;
}): React.ReactNode => {
    const dispatch = useAppDispatch();

    const baseVariant = useAppSelector((rootState) => {
        return baseVariantSelector(rootState.configurator || defaults);
    });
    const selectedRoot = useAppSelector((rootState) => {
        return rootProductSelector(rootState.configurator || defaults);
    });
    const modal = useAppSelector((rootState) => {
        const configuratorState = rootState.configurator || defaults;
        return getUpsellInfoModal(configuratorState, {
            bundleID: configuratorState.ui.upsellInfoModalActiveBundle,
        });
    });
    const upgradeBundles = useAppSelector((rootState) => {
        const configuratorState = rootState.configurator || defaults;
        const baseVar = baseVariantSelector(configuratorState);
        const mod = getUpsellInfoModal(configuratorState, {
            bundleID: configuratorState.ui.upsellInfoModalActiveBundle,
        });
        return upgradeBundlesSelector(
            configuratorState.entities.concreteBundles,
            baseVar,
            mod ? mod.bundle_type : "",
        );
    });

    React.useEffect(() => {
        return () => {
            clearAllBodyScrollLocks();
        };
    }, []);

    const isOpen = modal !== null;
    const modalClasses = classNames({
        [styles.modalWrapper]:
            props.configuratorType !== ConfiguratorTypes.PANEL,
        [styles.panelWrapper]:
            props.configuratorType === ConfiguratorTypes.PANEL,
    });
    const overlayClasses = classNames({
        [styles.panelOverlay]:
            props.configuratorType === ConfiguratorTypes.PANEL,
    });

    return (
        <Modal
            overlayClassName={overlayClasses}
            className={modalClasses}
            isOpen={isOpen}
            onAfterOpen={onOpenPanel}
            onAfterClose={onClosePanel}
            onRequestClose={() => dispatch(hideUpsellInfoModal())}
            contentLabel={modal?.bundle_description}
        >
            <button
                aria-label="Close"
                className="close-btn"
                onClick={() => dispatch(hideUpsellInfoModal())}
            >
                <SVG
                    aria-hidden="true"
                    className="close-icon"
                    src={iconXClose}
                />
            </button>
            {modal && (
                <UpsellInfoModalContent
                    baseVariant={baseVariant}
                    selectedRoot={selectedRoot}
                    modal={modal}
                    selectedAddons={props.selectedAddons}
                    selectedUpgrade={props.selectedUpgrade}
                    updateSelectedAddons={(updates) =>
                        dispatch(updateSelectedAddons(updates))
                    }
                    setSelectedUpgrade={(productID) =>
                        dispatch(setSelectedUpgrade(productID))
                    }
                    upgradeBundles={upgradeBundles}
                />
            )}
        </Modal>
    );
};
